import React, { useEffect, useState, useMemo } from 'react';
import { FiRefreshCcw, FiTrendingUp, FiTrendingDown, FiUsers, FiSearch, FiShield, FiAlertCircle, FiX, FiEye, FiShoppingCart, FiMapPin } from 'react-icons/fi';
import Chart from 'react-apexcharts';
import { SearchLogService } from '../../../service/admin/searchLogService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { formatDate } from '../../../helper/utils';
import ExcelTable from '../../../component/library/excelTable';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import './style.css';

const SearchLog = () => {
  const message = useMessage();
  const [loading, setLoading] = useState(true);
  const [timeRange] = useState('all');
  const [dateFilterStart, setDateFilterStart] = useState('');
  const [dateFilterEnd, setDateFilterEnd] = useState('');
  const [analytics, setAnalytics] = useState(null);
  // additional tab-specific state with individual loading states
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [userAnalyticsLoading, setUserAnalyticsLoading] = useState(false);
  const [userAnalyticsCurrentPage, setUserAnalyticsCurrentPage] = useState(1);
  const [userAnalyticsRowsPerPage, setUserAnalyticsRowsPerPage] = useState(20);
  const [userAnalyticsSortBy, setUserAnalyticsSortBy] = useState('totalSearches');
  const [categoriesData, setCategoriesData] = useState(null);
  const [categoriesDataLoading, setCategoriesDataLoading] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const [securityDataLoading, setSecurityDataLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('keyword'); // 'keyword', 'email', or 'ip'
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDetailsCurrentPage, setSearchDetailsCurrentPage] = useState(1);
  const [searchDetailsRowsPerPage, setSearchDetailsRowsPerPage] = useState(20);

  // Product Intent tab state
  const [productIntentUsers, setProductIntentUsers] = useState(null);
  const [productIntentLoading, setProductIntentLoading] = useState(false);
  // const [productIntentSummary, setProductIntentSummary] = useState(null);
  // const [productIntentSummaryLoading, setProductIntentSummaryLoading] = useState(false);
  const [productIntentRange, setProductIntentRange] = useState('24h');
  const [productIntentStartDate, setProductIntentStartDate] = useState('');
  const [productIntentEndDate, setProductIntentEndDate] = useState('');
  const [productIntentRowsPerPage, setProductIntentRowsPerPage] = useState(20);
  const [productIntentCurrentPage, setProductIntentCurrentPage] = useState(1);
  const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'logged-in', 'unknown'
  const [productIntentDetailLoading, setProductIntentDetailLoading] = useState({}); // map of loading states for detail fetch
  const [productIntentSearchQuery, setProductIntentSearchQuery] = useState('');
  const [productIntentSortBy, setProductIntentSortBy] = useState('mostPdpOpens');
  
  // Image Search tab state
  const [imageSearchData, setImageSearchData] = useState(null);
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchCurrentPage, setImageSearchCurrentPage] = useState(1);
  const [imageSearchRowsPerPage, setImageSearchRowsPerPage] = useState(20);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  
  // All Keywords tab state
  const [allKeywordsData, setAllKeywordsData] = useState(null);
  const [allKeywordsLoading, setAllKeywordsLoading] = useState(false);
  const [allKeywordsCurrentPage, setAllKeywordsCurrentPage] = useState(1);
  const [allKeywordsRowsPerPage, setAllKeywordsRowsPerPage] = useState(20);
  const [allKeywordsSearchQuery, setAllKeywordsSearchQuery] = useState('');
  const [allKeywordsAppliedSearch, setAllKeywordsAppliedSearch] = useState('');
  const [allKeywordsUserType, setAllKeywordsUserType] = useState('all');
  const [allKeywordsSortBy, setAllKeywordsSortBy] = useState('lastSearched');
  const [allKeywordsSortOrder, setAllKeywordsSortOrder] = useState('desc');
  
  // Loading states for buttons
  const [buttonLoading, setButtonLoading] = useState({});
  // Modal state for user list
  // Modal state for user product details
  const [userProductModal, setUserProductModal] = useState({ open: false, userId: null, eventType: null, userEmail: null, products: [], loading: false });

  // Modal state for user cart
  const [userCartModal, setUserCartModal] = useState({ open: false, userId: null, userEmail: null, cartItems: [], loading: false });

  // Modal state for user search keywords
  const [userSearchKeywordsModal, setUserSearchKeywordsModal] = useState({ open: false, userId: null, userEmail: null, keywords: [], loading: false });

  // Modal state for search count timestamps (Search Details)
  const [searchCountTimestampsModal, setSearchCountTimestampsModal] = useState({ open: false, timestamps: [], searchType: '', value: 0 });

  // Hover state for search keywords count (in modal)
  const [hoveredKeywordId, setHoveredKeywordId] = useState(null);

  // Get current user ID for ExcelTable
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;
  const formatDateForInput = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const applyQuickDatePreset = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start = today;
    let end = today;

    if (preset === 'yesterday') {
      start = new Date(today);
      start.setDate(start.getDate() - 1);
      end = new Date(start);
    } else if (preset === 'this-week') {
      // Keep this as a 7-day range including today
      start = new Date(today);
      start.setDate(today.getDate() - 6);
      end = new Date(today);
    } else if (preset === 'this-month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    }

    setDateFilterStart(formatDateForInput(start));
    setDateFilterEnd(formatDateForInput(end));
  };

  const dateFilterParams = useMemo(
    () => ({
      startDate: dateFilterStart || undefined,
      endDate: dateFilterEnd || undefined,
    }),
    [dateFilterStart, dateFilterEnd]
  );
  const activeDatePreset = useMemo(() => {
    if (!dateFilterStart && !dateFilterEnd) return 'all';
    if (!dateFilterStart || !dateFilterEnd) return null;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - 6);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const todayStr = formatDateForInput(today);
    const yesterdayStr = formatDateForInput(yesterday);
    const weekStartStr = formatDateForInput(weekStart);
    const monthStartStr = formatDateForInput(monthStart);

    if (dateFilterStart === todayStr && dateFilterEnd === todayStr) return 'today';
    if (dateFilterStart === yesterdayStr && dateFilterEnd === yesterdayStr) return 'yesterday';
    if (dateFilterStart === weekStartStr && dateFilterEnd === todayStr) return 'this-week';
    if (dateFilterStart === monthStartStr && dateFilterEnd === todayStr) return 'this-month';

    return null;
  }, [dateFilterStart, dateFilterEnd]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await SearchLogService.getAnalytics(timeRange, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        setAnalytics(response?.result?.data || response?.data);
      } else {
        message.showToast('Failed to load analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      message.showToast('Error loading search log analytics', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAnalytics = async (page = 1) => {
    try {
      setUserAnalyticsLoading(true);
      const response = await SearchLogService.getUserAnalytics(
        timeRange,
        dateFilterParams,
        page,
        userAnalyticsRowsPerPage,
        userAnalyticsSortBy
      );
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        setUserAnalytics(response?.result?.data || response?.data);
        setUserAnalyticsCurrentPage(page);
      } else {
        message.showToast('Failed to load user analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      message.showToast('Error loading user analytics', ToastTypes.ERROR);
    } finally {
      setUserAnalyticsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesDataLoading(true);
      const response = await SearchLogService.getCategories(timeRange, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        setCategoriesData(response?.result?.data || response?.data);
      } else {
        message.showToast('Failed to load category analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error fetching category analytics:', error);
      message.showToast('Error loading category analytics', ToastTypes.ERROR);
    } finally {
      setCategoriesDataLoading(false);
    }
  };

  const fetchSecurity = async () => {
    try {
      setSecurityDataLoading(true);
      const response = await SearchLogService.getSecurity(timeRange, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        setSecurityData(response?.result?.data || response?.data);
      } else {
        message.showToast('Failed to load security analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error fetching security analytics:', error);
      message.showToast('Error loading security analytics', ToastTypes.ERROR);
    } finally {
      setSecurityDataLoading(false);
    }
  };

  const fetchImageSearch = async (page = 1, limit = imageSearchRowsPerPage, search = imageSearchQuery) => {
    try {
      setImageSearchLoading(true);
      const response = await SearchLogService.getImageSearchAnalytics(timeRange, page, limit, search, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const data = response?.result?.data || response?.data;
        setImageSearchData(data);
        setImageSearchCurrentPage(page);
      } else {
        message.showToast('Failed to load image search analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error fetching image search analytics:', error);
      message.showToast('Error loading image search analytics', ToastTypes.ERROR);
    } finally {
      setImageSearchLoading(false);
    }
  };

  const fetchImageProducts = (imageId) => {
    const query = new URLSearchParams({
      type: 'image-search',
      imageId: imageId || '',
    });
    window.open(`/admin/search-log/user-details?${query.toString()}`, '_blank');
  };

  useEffect(() => {
    // always refresh overview stats when the time filter changes
    fetchAnalytics();
  }, [timeRange, dateFilterStart, dateFilterEnd]);

  useEffect(() => {
    // fetch data for the currently visible tab when it changes or when filter updates
    if (activeTab === 'users') {
      setUserAnalyticsCurrentPage(1);
      fetchUserAnalytics(1);
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'security') {
      fetchSecurity();
    } else if (activeTab === 'image-search') {
      // reset page to 1 whenever timeRange or rowsPerPage changes
      setImageSearchCurrentPage(1);
      fetchImageSearch(1, imageSearchRowsPerPage, imageSearchQuery);
    } else if (activeTab === 'all-keywords') {
      fetchAllKeywords();
    }
  }, [activeTab, timeRange, dateFilterStart, dateFilterEnd, imageSearchRowsPerPage, userAnalyticsRowsPerPage, userAnalyticsSortBy]);

  // useEffect(() => {
  //   if (activeTab === 'product-intent') {
  //     fetchProductIntentData();
  //     // fetchProductIntentSummary();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [activeTab, timeRange, dateFilterStart, dateFilterEnd]);

  // Refresh search results when timeRange changes (if there's an active search)
  useEffect(() => {
    if (activeTab === 'search' && searchResults && searchQuery.trim()) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, dateFilterStart, dateFilterEnd]);

  // Fetch product intent data
  const fetchProductIntentData = async (page = 1, search = productIntentSearchQuery) => {
    try {
      setProductIntentLoading(true);
      const params = {
        range: dateFilterStart && dateFilterEnd ? 'custom' : timeRange,
        startDate: dateFilterStart || undefined,
        endDate: dateFilterEnd || undefined,
        userType: userTypeFilter,
        page: page,
        limit: productIntentRowsPerPage,
        search: search.trim() || undefined,
        sortBy: productIntentSortBy,
      };

      const usersRes = await SearchLogService.getProductIntentUsers(params);

      if (usersRes?.result?.code === 200 || usersRes?.status?.code === 200) {
        const usersData = usersRes?.result?.data || usersRes?.data;

        setProductIntentUsers({
          ...usersData,
          totalCount: usersData.pagination?.totalCount || usersData.users?.length || 0,
        });

        setProductIntentCurrentPage(page);
      } else {
        message.showToast('Error loading product intent analytics', ToastTypes.ERROR);
      }
    } catch (error) {
      message.showToast('Error loading product intent analytics', ToastTypes.ERROR);
    } finally {
      setProductIntentLoading(false);
    }
  };

  // Fetch summary counts for product intent (disabled - not used in UI)
  // const fetchProductIntentSummary = async () => {
  //   try {
  //     setProductIntentSummaryLoading(true);
  //     const params = {
  //       range: dateFilterStart && dateFilterEnd ? 'custom' : timeRange,
  //       startDate: dateFilterStart || undefined,
  //       endDate: dateFilterEnd || undefined,
  //     };
  //     const response = await SearchLogService.getProductIntentSummary(params);
  //     if (response?.result?.code === 200 || response?.status?.code === 200) {
  //       setProductIntentSummary(response?.result?.data || response?.data);
  //     } else {
  //       message.showToast('Error loading product intent summary', ToastTypes.ERROR);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching product intent summary:', err);
  //     message.showToast('Error loading product intent summary', ToastTypes.ERROR);
  //   } finally {
  //     setProductIntentSummaryLoading(false);
  //   }
  // };

  // Fetch all keywords data
  const fetchAllKeywords = async (page = 1) => {
    try {
      setAllKeywordsLoading(true);
      const params = {
        page: page,
        limit: allKeywordsRowsPerPage,
        search: allKeywordsAppliedSearch,
        userType: allKeywordsUserType,
        sortBy: allKeywordsSortBy,
        sortOrder: allKeywordsSortOrder,
        timeRange: timeRange,
        startDate: dateFilterStart || undefined,
        endDate: dateFilterEnd || undefined,
      };

      const response = await SearchLogService.getAllKeywords(params);

      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const data = response?.result?.data || response?.data;
        setAllKeywordsData(data);
        setAllKeywordsCurrentPage(page);
      } else {
        message.showToast('Error loading all keywords', ToastTypes.ERROR);
      }
    } catch (error) {
      message.showToast('Error loading all keywords', ToastTypes.ERROR);
    } finally {
      setAllKeywordsLoading(false);
    }
  };

  // Open user list modal for keyword
  // Open user product details in new tab
  const openUserProductDetailsInNewTab = (userId, eventType, userEmail) => {
    const selectedRange = dateFilterStart && dateFilterEnd ? 'custom' : timeRange;
    const query = new URLSearchParams({
      type: eventType,
      userId: userId || '',
      userEmail: userEmail || 'User',
      range: selectedRange,
      page: '1',
      limit: '10',
    });

    if (selectedRange === 'custom' && dateFilterStart && dateFilterEnd) {
      query.set('startDate', dateFilterStart);
      query.set('endDate', dateFilterEnd);
    }

    window.open(`/admin/search-log/user-details?${query.toString()}`, '_blank');
  };

  // Fetch user product details for modal (kept for backward compatibility)
  const fetchUserProductDetails = async (userId, eventType, userEmail) => {
    openUserProductDetailsInNewTab(userId, eventType, userEmail);
  };

  // Open user cart in new tab
  const openUserCartInNewTab = (userId, userEmail) => {
    const query = new URLSearchParams({
      type: 'cart',
      userId: userId || '',
      userEmail: userEmail || 'User',
      page: '1',
      limit: '10',
    });
    window.open(`/admin/search-log/user-details?${query.toString()}`, '_blank');
  };

  // Fetch user cart for modal (kept for backward compatibility)
  const fetchUserCart = async (userId, userEmail) => {
    openUserCartInNewTab(userId, userEmail);
  };

  // Open user search keywords in new tab
  const openUserSearchKeywordsInNewTab = (identifier, userId) => {
    const query = new URLSearchParams({
      type: 'searchKeywords',
      identifier: identifier || '',
      userId: userId || '',
      userEmail: identifier || 'User',
      page: '1',
      limit: '10',
    });
    window.open(`/admin/search-log/user-details?${query.toString()}`, '_blank');
  };

  // Fetch user search keywords for modal (kept for backward compatibility)
  const fetchUserSearchKeywords = async (identifier, userId) => {
    openUserSearchKeywordsInNewTab(identifier, userId);
  };

  useEffect(() => {
    if (activeTab === 'product-intent') {
      setProductIntentCurrentPage(1);
      fetchProductIntentData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, timeRange, dateFilterStart, dateFilterEnd, productIntentRowsPerPage, userTypeFilter, productIntentSortBy]);

  useEffect(() => {
    if (activeTab === 'all-keywords') {
      setAllKeywordsCurrentPage(1); // Reset to first page when filters change
      fetchAllKeywords(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeywordsAppliedSearch, allKeywordsUserType, allKeywordsSortBy, allKeywordsSortOrder, allKeywordsRowsPerPage, timeRange, dateFilterStart, dateFilterEnd]);

  const handleBlockKeyword = async (keyword, isBlocked) => {
    try {
      const response = await SearchLogService.toggleKeywordBlock(keyword, !isBlocked);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        message.showToast(`Keyword ${!isBlocked ? 'blocked' : 'unblocked'} successfully`, ToastTypes.SUCCESS);
        // refresh data depending on current tab
        switch (activeTab) {
          case 'security':
            fetchSecurity();
            break;
          case 'users':
            fetchUserAnalytics(userAnalyticsCurrentPage);
            break;
          case 'all-keywords':
            fetchAllKeywords();
            break;
          case 'overview':
          default:
            fetchAnalytics();
        }
      } else {
        message.showToast('Failed to update keyword', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Error blocking keyword:', error);
      message.showToast('Error updating keyword', ToastTypes.ERROR);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      message.showToast('Please enter a search query', ToastTypes.ERROR);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await SearchLogService.searchByKeywordOrEmail(searchQuery.trim(), searchType, timeRange, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const data = response?.result?.data || response?.data;
        setSearchDetailsCurrentPage(1);
        setSearchResults(data);
      } else {
        message.showToast('Failed to search', ToastTypes.ERROR);
        setSearchResults(null);
      }
    } catch (error) {
      console.error('Error searching:', error);
      message.showToast('Error performing search', ToastTypes.ERROR);
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchCountClick = async (keyword) => {
    // Set the search type and query
    setSearchType('keyword');
    setSearchQuery(keyword);
    setActiveTab('search');
    setSearchResults(null);

    // Perform the search with the keyword
    try {
      setSearchLoading(true);
      const response = await SearchLogService.searchByKeywordOrEmail(keyword.trim(), 'keyword', timeRange, dateFilterParams);
      if (response?.result?.code === 200 || response?.status?.code === 200) {
        const data = response?.result?.data || response?.data;
        setSearchDetailsCurrentPage(1);
        setSearchResults(data);
      } else {
        message.showToast('Failed to search', ToastTypes.ERROR);
        setSearchResults(null);
      }
    } catch (error) {
      console.error('Error searching:', error);
      message.showToast('Error performing search', ToastTypes.ERROR);
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchCountTimestampsClick = (timestamps, searchType, value) => {
    setSearchCountTimestampsModal({
      open: true,
      timestamps: timestamps || [],
      searchType,
      value,
    });
  };

  const searchDetailsData = useMemo(() => {
    if (!searchResults) return [];
    if (searchType === 'keyword') return Array.isArray(searchResults.users) ? searchResults.users : [];
    return Array.isArray(searchResults.keywords) ? searchResults.keywords : [];
  }, [searchResults, searchType]);

  const searchDetailsTotalPages = Math.max(1, Math.ceil(searchDetailsData.length / searchDetailsRowsPerPage));

  const paginatedSearchDetailsData = useMemo(() => {
    const start = (searchDetailsCurrentPage - 1) * searchDetailsRowsPerPage;
    return searchDetailsData.slice(start, start + searchDetailsRowsPerPage);
  }, [searchDetailsData, searchDetailsCurrentPage, searchDetailsRowsPerPage]);

  useEffect(() => {
    if (searchDetailsCurrentPage > searchDetailsTotalPages) {
      setSearchDetailsCurrentPage(searchDetailsTotalPages);
    }
  }, [searchDetailsCurrentPage, searchDetailsTotalPages]);

  if (loading && !analytics) {
    return (
      <div className="search-log-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="search-log-page">
        <div className="error-container">
          <p>Failed to load analytics. Please try again.</p>
          <button onClick={fetchAnalytics} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, trend, subtitle }) => (
    <div className="search-log-stat-card">
      <div className="stat-header">
        <div className="stat-icon">
          <Icon />
        </div>
        <div className="search-log-stat-content">
          <h3>{title}</h3>
          <p className="stat-value">{value?.toLocaleString() || 0}</p>
          {subtitle && <p className="stat-subtitle">{subtitle}</p>}
        </div>
      </div>
      {trend && (
        <div className={`stat-trend ${trend > 0 ? 'up' : 'down'}`}>
          {trend > 0 ? <FiTrendingUp /> : <FiTrendingDown />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );

  const TableCard = ({
    title,
    data,
    columns,
    renderRow,
    emptyMessage = 'No data available',
    limit = 20, // max rows
    loading = false, // loading state for the card
  }) => {
    const limitedData = Array.isArray(data) ? data.slice(0, limit) : [];

    // Render loading skeleton
    if (loading) {
      return (
        <div className="table-card">
          <h3 className="table-title">{title}</h3>
          <div className="loading-skeleton">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {columns.map((col, idx) => (
                      <th key={idx}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...Array(5)].map((_, idx) => (
                    <tr key={idx}>
                      {columns.map((_, cellIdx) => (
                        <td key={cellIdx}>
                          <div className="skeleton-line"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="table-card">
        <h3 className="table-title">{title}</h3>
        {!limitedData || limitedData.length === 0 ? (
          <div className="empty-state">{emptyMessage}</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {columns.map((col, idx) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{limitedData.map((item, idx) => renderRow(item, idx))}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'all-keywords', label: 'All Keywords' },
    { id: 'users', label: 'User Analytics' },
    { id: 'categories', label: 'Categories' },
    { id: 'security', label: 'Security' },
    { id: 'search', label: 'Search Details' },

    { id: 'product-intent', label: 'Product Intent' },
    { id: 'image-search', label: 'Image Search' },
  ];

  return (
    <div className="search-log-page">
      {/* Filter Actions */}
      <div className="search-log-actions">
        <div className="date-filter-group">
          <div className="quick-date-preset-group">
            <button
              type="button"
              className={`quick-date-preset-btn ${activeDatePreset === 'all' ? 'active' : ''}`}
              onClick={() => {
                setDateFilterStart('');
                setDateFilterEnd('');
              }}
            >
              All
            </button>
            <button
              type="button"
              className={`quick-date-preset-btn ${activeDatePreset === 'today' ? 'active' : ''}`}
              onClick={() => applyQuickDatePreset('today')}
            >
              Today
            </button>
            <button
              type="button"
              className={`quick-date-preset-btn ${activeDatePreset === 'yesterday' ? 'active' : ''}`}
              onClick={() => applyQuickDatePreset('yesterday')}
            >
              Yesterday
            </button>
            <button
              type="button"
              className={`quick-date-preset-btn ${activeDatePreset === 'this-week' ? 'active' : ''}`}
              onClick={() => applyQuickDatePreset('this-week')}
            >
              This Week
            </button>
            <button
              type="button"
              className={`quick-date-preset-btn ${activeDatePreset === 'this-month' ? 'active' : ''}`}
              onClick={() => applyQuickDatePreset('this-month')}
            >
              This Month
            </button>
          </div>
          <input
            type="date"
            value={dateFilterStart}
            onChange={e => setDateFilterStart(e.target.value)}
            className="date-filter-input"
            placeholder="From date"
            title="From date"
          />
          <span className="date-filter-separator">to</span>
          <input
            type="date"
            value={dateFilterEnd}
            onChange={e => setDateFilterEnd(e.target.value)}
            className="date-filter-input"
            placeholder="To date"
            title="To date"
          />
          {(dateFilterStart || dateFilterEnd) && (
            <button
              type="button"
              className="clear-date-filter-btn"
              onClick={() => {
                setDateFilterStart('');
                setDateFilterEnd('');
              }}
            >
              Clear Date
            </button>
          )}
        </div>
        <button
          onClick={() => {
            switch (activeTab) {
              case 'overview':
                fetchAnalytics();
                break;
              case 'users':
                fetchUserAnalytics(userAnalyticsCurrentPage);
                break;
              case 'categories':
                fetchCategories();
                break;
              case 'security':
                fetchSecurity();
                break;
              case 'image-search':
                fetchImageSearch(imageSearchCurrentPage, imageSearchRowsPerPage, imageSearchQuery);
                break;
              case 'product-intent':
                fetchProductIntentData();
                break;
              case 'all-keywords':
                fetchAllKeywords();
                break;
              default:
                fetchAnalytics();
            }
          }}
          className="refresh-btn"
          disabled={loading || tabLoading || allKeywordsLoading || imageSearchLoading}
        >
          <FiRefreshCcw className={loading || tabLoading || allKeywordsLoading || imageSearchLoading ? 'spinning' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Overview Stats - Visible for all tabs */}
      {analytics.overview && (
        <div className="stats-grid">
          <StatCard title="Total Keywords" value={analytics.overview.totalKeywords} icon={FiSearch} />
          <StatCard title="Total Searches" value={analytics.overview.totalSearches} icon={FiTrendingUp} />
          <StatCard title="Logged-in Users" value={analytics.overview.uniqueLoggedInUsers} icon={FiUsers} />
          <StatCard title="Unique IPs" value={analytics.overview.uniqueIPs} icon={FiUsers} subtitle="Anonymous users" />
        </div>
      )}

      {/* Tabs */}
      <div className="analytics-tabs">
        {tabs.map(tab => (
          <button key={tab.id} className={`search-log-tab-btn ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="content-grid">
              <TableCard
                title="Top Searched Keywords"
                data={analytics.topSearchedKeywords?.slice(0, 10)}
                loading={loading}
                // columns={['Rank', 'Keyword', 'Searches', 'Category', 'Last Searched', 'Action']}
                columns={['Rank', 'Keyword', 'Searches', 'Last Searched', 'Action']}
                renderRow={(item, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <strong>{item.keyword}</strong>
                    </td>
                    <td>{item.searchCount}</td>
                    {/* <td>
                      <span className="badge">{(item.category === 'alibaba' ? 'global' : item.category) || 'global'}</span>
                    </td> */}
                    <td>{formatDate(item.lastSearched)}</td>
                    <td>
                      {item.isBlocked ? (
                        <button className="search-log-btn-sm search-log-btn-blocked" onClick={() => handleBlockKeyword(item.keyword, true)} title="Unblock keyword">
                          Blocked
                        </button>
                      ) : (
                        <button className="search-log-btn-sm search-log-btn-danger" onClick={() => handleBlockKeyword(item.keyword, false)} title="Block keyword">
                          Block
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              />

              <TableCard
                title="Emerging Keywords"
                data={analytics.emergingKeywords?.slice(0, 10)}
                loading={loading}
                columns={['Keyword', 'Searches', 'Category', 'Created']}
                renderRow={(item, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{item.keyword}</strong>
                    </td>
                    <td>{item.searchCount}</td>
                    <td>
                      <span className="badge">{(item.category === 'alibaba' ? 'global' : item.category) || 'global'}</span>
                    </td>
                    <td>{formatDate(item.createdAt)}</td>
                  </tr>
                )}
              />
            </div>
          </div>
        )}

        {/* All Keywords Tab */}
        {activeTab === 'all-keywords' && (
          <div className="all-keywords-content">
            {allKeywordsLoading && !allKeywordsData ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading all keywords...</p>
              </div>
            ) : !allKeywordsData ? (
              <div className="error-container">
                <p>Failed to load keywords. Please try again.</p>
                <button onClick={() => fetchAllKeywords(1)} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="search-log-toolbar" style={{ marginBottom: '10px' }}>
                  {/* Search */}
                  <div className="search-log-search-wrap" style={{ minWidth: '220px', maxWidth: '300px' }}>
                    <FiSearch style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '11px', pointerEvents: 'none' }} />
                    <input
                      type="text"
                      placeholder="Search keywords..."
                      value={allKeywordsSearchQuery}
                      onChange={(e) => setAllKeywordsSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setAllKeywordsAppliedSearch(allKeywordsSearchQuery.trim());
                          setAllKeywordsCurrentPage(1);
                        }
                      }}
                      className="search-log-search-input"
                    />
                    {allKeywordsSearchQuery && (
                      <FiX
                        onClick={() => {
                          setAllKeywordsSearchQuery('');
                          setAllKeywordsAppliedSearch('');
                          setAllKeywordsCurrentPage(1);
                        }}
                        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }}
                      />
                    )}
                  </div>
                  <button
                    className="search-log-action-btn search-log-action-btn--outline"
                    onClick={() => { setAllKeywordsAppliedSearch(allKeywordsSearchQuery.trim()); setAllKeywordsCurrentPage(1); }}
                    disabled={allKeywordsLoading}
                  >
                    <FiSearch /> Search
                  </button>
                  <select
                    value={allKeywordsUserType}
                    onChange={(e) => { setAllKeywordsUserType(e.target.value); setAllKeywordsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="all">All Users</option>
                    <option value="logged-in">Logged-in Users</option>
                    <option value="anonymous">Anonymous Users</option>
                  </select>
                  <select
                    value={allKeywordsSortBy}
                    onChange={(e) => { setAllKeywordsSortBy(e.target.value); setAllKeywordsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="lastSearched">Recent Search First</option>
                    <option value="searchCount">Most Search Count</option>
                    <option value="userCount">Most User Count</option>
                  </select>
                  <label style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Rows:</label>
                  <select
                    value={allKeywordsRowsPerPage}
                    onChange={(e) => { const val = Number(e.target.value); setAllKeywordsRowsPerPage(val); setAllKeywordsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <button
                    className="search-log-action-btn search-log-action-btn--outline"
                    onClick={() => fetchAllKeywords(allKeywordsCurrentPage)}
                    disabled={allKeywordsLoading}
                    title="Refresh data"
                  >
                    <FiRefreshCcw />
                  </button>
                </div>

                {/* Table */}
                <div className="search-log-tbl-wrap">
                  <table className="search-log-tbl">
                    <thead>
                      <tr>
                        <th>Keyword</th>
                        <th>Search Count</th>
                        <th>User Count</th>
                        <th>Recent Search By</th>
                        <th>Last Searched</th>
                        <th>First Searched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allKeywordsLoading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                          <tr key={i} className="search-log-tbl-loading">
                            {Array.from({ length: 6 }).map((__, j) => (
                              <td key={j}><span className="search-log-skeleton" /></td>
                            ))}
                          </tr>
                        ))
                      ) : (allKeywordsData.keywords || []).length === 0 ? (
                        <tr className="search-log-tbl-empty">
                          <td colSpan={6}>No keywords found</td>
                        </tr>
                      ) : (
                        (allKeywordsData.keywords || []).map((row, idx) => (
                          <tr key={idx}>
                            <td><strong>{row.keyword}</strong></td>
                            <td>
                              <span
                                style={{ cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline', fontWeight: '500' }}
                                onClick={(e) => { e.stopPropagation(); handleSearchCountClick(row.keyword); }}
                                title="Click to view search details"
                              >
                                {row.searchCount?.toLocaleString() || 0}
                              </span>
                            </td>
                            <td>
                              <span
                                style={{ cursor: 'pointer', color: '#3b82f6', textDecoration: 'underline', fontWeight: '500' }}
                                onClick={(e) => { e.stopPropagation(); handleSearchCountClick(row.keyword); }}
                                title="Click to view search details"
                              >
                                {row.userCount?.toLocaleString() || 0}
                              </span>
                            </td>
                            <td>{row.recentSearchBy || 'N/A'}</td>
                            <td>{formatDate(row.lastSearched)}</td>
                            <td>{formatDate(row.firstSearched)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {allKeywordsData.pagination && allKeywordsData.pagination.totalCount > allKeywordsRowsPerPage && (
                  <div className="search-log-pagination">
                    <span className="search-log-pagination-info">
                      Showing {(allKeywordsCurrentPage - 1) * allKeywordsRowsPerPage + 1}–{Math.min(allKeywordsCurrentPage * allKeywordsRowsPerPage, allKeywordsData.pagination.totalCount)} of {allKeywordsData.pagination.totalCount}
                    </span>
                    <div className="search-log-pagination-btns">
                      <button className="search-log-pg-btn" onClick={() => { setAllKeywordsCurrentPage(allKeywordsCurrentPage - 1); fetchAllKeywords(allKeywordsCurrentPage - 1); }} disabled={allKeywordsCurrentPage === 1 || allKeywordsLoading}>Previous</button>
                      <span>Page {allKeywordsCurrentPage} of {Math.ceil(allKeywordsData.pagination.totalCount / allKeywordsRowsPerPage)}</span>
                      <button className="search-log-pg-btn" onClick={() => { setAllKeywordsCurrentPage(allKeywordsCurrentPage + 1); fetchAllKeywords(allKeywordsCurrentPage + 1); }} disabled={allKeywordsCurrentPage >= Math.ceil(allKeywordsData.pagination.totalCount / allKeywordsRowsPerPage) || allKeywordsLoading}>Next</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-content">
            {userAnalyticsLoading && !userAnalytics ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading user analytics...</p>
              </div>
            ) : !userAnalytics ? (
              <div className="error-container">
                <p>Failed to load user analytics. Please try again.</p>
                <button onClick={() => fetchUserAnalytics(1)} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="search-log-toolbar" style={{ marginBottom: '10px' }}>
                  <select
                    value={userAnalyticsSortBy}
                    onChange={(e) => { setUserAnalyticsSortBy(e.target.value); setUserAnalyticsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer', minWidth: '190px' }}
                  >
                    <option value="totalSearches">Top By Total Searches</option>
                    <option value="uniqueKeywords">Top By Unique Keywords</option>
                  </select>
                  <label style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Rows:</label>
                  <select
                    value={userAnalyticsRowsPerPage}
                    onChange={(e) => { const val = Number(e.target.value); setUserAnalyticsRowsPerPage(val); setUserAnalyticsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <button
                    className="search-log-action-btn search-log-action-btn--outline"
                    onClick={() => fetchUserAnalytics(userAnalyticsCurrentPage)}
                    disabled={userAnalyticsLoading}
                    title="Refresh data"
                  >
                    <FiRefreshCcw />
                  </button>
                </div>

                {/* Table */}
                <div className="search-log-tbl-wrap">
                  <table className="search-log-tbl">
                    <thead>
                      <tr>
                        <th>User Email</th>
                        <th>Phone</th>
                        <th>Unique ID</th>
                        <th>User IP</th>
                        <th>Total Searches</th>
                        <th>Unique Keywords</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userAnalyticsLoading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                          <tr key={i} className="search-log-tbl-loading">
                            {Array.from({ length: 6 }).map((__, j) => (
                              <td key={j}><span className="search-log-skeleton" /></td>
                            ))}
                          </tr>
                        ))
                      ) : (userAnalytics?.powerUsers || []).length === 0 ? (
                        <tr className="search-log-tbl-empty"><td colSpan={6}>No users found</td></tr>
                      ) : (
                        (userAnalytics?.powerUsers || []).map((row, idx) => (
                          <tr key={idx}>
                            <td><strong>{row.email || 'N/A'}</strong></td>
                            <td>{row.phone || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                            <td>{row.uniqueId ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '11px' }}>{row.uniqueId}</span> : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                            <td>{row.ip || 'N/A'}</td>
                            <td>{row.totalSearches}</td>
                            <td>{row.uniqueKeywords}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {userAnalytics?.pagination && userAnalytics.pagination.totalCount > userAnalyticsRowsPerPage && (
                  <div
                    style={{
                      marginTop: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      Showing {(userAnalyticsCurrentPage - 1) * userAnalyticsRowsPerPage + 1} to{' '}
                      {Math.min(userAnalyticsCurrentPage * userAnalyticsRowsPerPage, userAnalytics.pagination.totalCount)} of{' '}
                      {userAnalytics.pagination.totalCount}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => fetchUserAnalytics(userAnalyticsCurrentPage - 1)}
                        disabled={userAnalyticsCurrentPage === 1 || userAnalyticsLoading}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          backgroundColor: userAnalyticsCurrentPage === 1 ? '#f1f5f9' : '#fff',
                          color: userAnalyticsCurrentPage === 1 ? '#94a3b8' : '#475569',
                          cursor: userAnalyticsCurrentPage === 1 || userAnalyticsLoading ? 'not-allowed' : 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '13px', color: '#475569' }}>
                        Page {userAnalyticsCurrentPage} of {Math.max(userAnalytics.pagination.totalPages || 1, 1)}
                      </span>
                      <button
                        onClick={() => fetchUserAnalytics(userAnalyticsCurrentPage + 1)}
                        disabled={userAnalyticsCurrentPage >= Math.max(userAnalytics.pagination.totalPages || 1, 1) || userAnalyticsLoading}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          backgroundColor:
                            userAnalyticsCurrentPage >= Math.max(userAnalytics.pagination.totalPages || 1, 1) ? '#f1f5f9' : '#fff',
                          color:
                            userAnalyticsCurrentPage >= Math.max(userAnalytics.pagination.totalPages || 1, 1) ? '#94a3b8' : '#475569',
                          cursor:
                            userAnalyticsCurrentPage >= Math.max(userAnalytics.pagination.totalPages || 1, 1) || userAnalyticsLoading
                              ? 'not-allowed'
                              : 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="categories-content">
            <div className="content-grid">
              {/* Global Category Only */}
              {categoriesData?.categoryWiseSearches
                ?.filter(cat => cat.category === 'global')
                .map((category, idx) => (
                  <TableCard
                    key={idx}
                    title={`Global (${category.totalSearches} searches)`}
                    data={category.topKeywords}
                    loading={categoriesDataLoading}
                    columns={['Keyword', 'Searches', 'Last Searched']}
                    renderRow={(item, itemIdx) => (
                      <tr key={itemIdx}>
                        <td>
                          <strong>{item.keyword}</strong>
                        </td>
                        <td>{item.searchCount}</td>
                        <td>{formatDate(item.lastSearched)}</td>
                      </tr>
                    )}
                  />
                ))}

              <TableCard
                title="Source-wise Statistics"
                data={categoriesData?.sourceWiseSearches}
                loading={categoriesDataLoading}
                columns={['Source', 'Total Searches', 'Unique Keywords']}
                renderRow={(item, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{item.source === 'alibaba' ? 'global' : item.source}</strong>
                    </td>
                    <td>{item.totalSearches}</td>
                    <td>{item.uniqueKeywords}</td>
                  </tr>
                )}
              />
            </div>
          </div>
        )}

        {/* Image Search Tab */}
        {activeTab === 'image-search' && (
          <div>
            {imageSearchLoading && !imageSearchData ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading image search analytics...</p>
              </div>
            ) : !imageSearchData ? (
              <div className="error-container">
                <p>Failed to load image search analytics. Please try again.</p>
                <button onClick={() => fetchImageSearch(1, imageSearchRowsPerPage, imageSearchQuery)} className="retry-btn">
                  Retry
                </button>
              </div>
            ) : (
              <>
                {/* Image Search Table */}
                <>
                    {/* Combined stats + toolbar row */}
                    <div className="search-log-img-topbar">
                      {/* Stats */}
                      <div className="search-log-img-stats">
                        <span className="search-log-img-stat">Total Image Searches: <strong>{(imageSearchData.overview?.totalImageSearches || 0).toLocaleString()}</strong></span>
                        <span className="search-log-img-stat-sep">|</span>
                        <span className="search-log-img-stat">Logged-in Users: <strong>{(imageSearchData.overview?.totalLoggedInUsers || 0).toLocaleString()}</strong></span>
                        <span className="search-log-img-stat-sep">|</span>
                        <span className="search-log-img-stat">Anonymous Users: <strong>{(imageSearchData.overview?.totalAnonymousUsers || 0).toLocaleString()}</strong></span>
                      </div>

                      {/* Controls */}
                      <div className="search-log-img-controls">
                        <div className="search-log-search-wrap" style={{ minWidth: '200px', maxWidth: '260px' }}>
                          <FiSearch style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '11px', pointerEvents: 'none' }} />
                          <input
                            type="text"
                            placeholder="Search by username or email..."
                            value={imageSearchQuery}
                            onChange={(e) => setImageSearchQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { setImageSearchCurrentPage(1); fetchImageSearch(1, imageSearchRowsPerPage, imageSearchQuery.trim()); } }}
                            className="search-log-search-input"
                          />
                          {imageSearchQuery && (
                            <FiX onClick={() => setImageSearchQuery('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }} />
                          )}
                        </div>
                        <button
                          className="search-log-action-btn search-log-action-btn--outline"
                          onClick={() => { setImageSearchCurrentPage(1); fetchImageSearch(1, imageSearchRowsPerPage, imageSearchQuery.trim()); }}
                          disabled={imageSearchLoading}
                        >
                          <FiSearch /> Search
                        </button>
                        <label style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Rows:</label>
                        <select
                          value={imageSearchRowsPerPage}
                          onChange={(e) => { const val = Number(e.target.value); setImageSearchRowsPerPage(val); setImageSearchCurrentPage(1); fetchImageSearch(1, val, imageSearchQuery); }}
                          className="search-log-action-btn search-log-action-btn--outline"
                          style={{ cursor: 'pointer' }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <button
                          className="search-log-action-btn search-log-action-btn--outline"
                          onClick={() => fetchImageSearch(imageSearchCurrentPage, imageSearchRowsPerPage, imageSearchQuery)}
                          disabled={imageSearchLoading}
                          title="Refresh"
                        >
                          <FiRefreshCcw className={imageSearchLoading ? 'spinning' : ''} />
                        </button>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="search-log-tbl-wrap">
                      <table className="search-log-tbl">
                        <thead>
                          <tr>
                            <th>Search Image</th>
                            <th>Image ID</th>
                            <th>Users</th>
                            <th>Total Products</th>
                            <th>Searches</th>
                            <th>Logged-in Users</th>
                            <th>Anonymous Users</th>
                            <th>Last Searched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {imageSearchLoading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                              <tr key={i} className="search-log-tbl-loading">
                                {Array.from({ length: 8 }).map((__, j) => (
                                  <td key={j}><span className="search-log-skeleton" /></td>
                                ))}
                              </tr>
                            ))
                          ) : (imageSearchData.imageSearches || []).length === 0 ? (
                            <tr className="search-log-tbl-empty"><td colSpan={8}>No image search data found</td></tr>
                          ) : (
                            (imageSearchData.imageSearches || []).map((row, idx) => {
                              const value = row.userDetails;
                              let userCell = <span style={{ color: '#94a3b8' }}>No users</span>;
                              if (value && value.length > 0) {
                                const firstUser = value[0];
                                const firstName = firstUser.firstName || '';
                                const lastName = firstUser.lastName || '';
                                const userName = firstUser.userName || '';
                                const displayName = firstName && lastName ? `${firstName} ${lastName}` : userName || '';
                                const email = firstUser.email || firstUser.ip || 'Anonymous';
                                const phone = firstUser.phone || '';
                                const uniqueId = firstUser.uniqueId || '';
                                const isAnonymous = !firstUser.userId;
                                let locationLine1 = isAnonymous ? firstUser.locationLine1 : null;
                                let locationLine2 = isAnonymous ? firstUser.locationLine2 : null;
                                if (isAnonymous && !locationLine1 && !locationLine2 && typeof firstUser.location === 'string' && firstUser.location) {
                                  const ci = firstUser.location.indexOf(',');
                                  if (ci >= 0) { locationLine1 = firstUser.location.slice(0, ci).trim(); locationLine2 = firstUser.location.slice(ci + 1).trim(); }
                                  else { locationLine1 = firstUser.location; }
                                }
                                const moreCount = value.length - 1;
                                const mapQuery = [locationLine1, locationLine2].filter(Boolean).join(', ');
                                const mapsUrl = mapQuery.trim().length > 0 ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}` : null;
                                userCell = (
                                  <div>
                                    {displayName && <div style={{ fontWeight: '600', marginBottom: '2px' }}>{displayName}</div>}
                                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px' }}>{email}</div>
                                    {(locationLine1 || locationLine2) && mapsUrl && (
                                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="search-log-location-map-link" style={{ marginBottom: '2px' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                                          <FiMapPin style={{ fontSize: '10px', color: '#3b82f6', marginTop: '2px', flexShrink: 0 }} />
                                          <div style={{ fontSize: '11px', lineHeight: 1.35 }}>
                                            {locationLine1 && <div style={{ color: '#64748b' }}>{locationLine1}</div>}
                                            {!locationLine1 && locationLine2 && <div style={{ color: '#94a3b8' }}>{locationLine2}</div>}
                                          </div>
                                        </div>
                                        {locationLine1 && locationLine2 && <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.35, marginTop: '1px' }}>{locationLine2}</div>}
                                      </a>
                                    )}
                                    {(phone || uniqueId) && (
                                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                                        {phone && <span>{phone}</span>}
                                        {phone && uniqueId && <span>  </span>}
                                        {uniqueId && <span>({uniqueId})</span>}
                                      </div>
                                    )}
                                    {moreCount > 0 && <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>+{moreCount} more {moreCount === 1 ? 'user' : 'users'}</div>}
                                  </div>
                                );
                              }
                              return (
                                <tr key={idx}>
                                  <td>
                                    {row.searchImageUrl ? (
                                      <a href={row.searchImageUrl} target="_blank" rel="noopener noreferrer">
                                        <img src={row.searchImageUrl} alt="Search" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                                      </a>
                                    ) : (
                                      <div style={{ width: '40px', height: '40px', background: '#f3f4f6', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#9ca3af', textAlign: 'center' }}>No Image</div>
                                    )}
                                  </td>
                                  <td>{row.imageId}</td>
                                  <td>{userCell}</td>
                                  <td>
                                    <button onClick={() => fetchImageProducts(row.imageId)} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline', fontSize: '11px', padding: 0 }}>
                                      {row.productCount?.toLocaleString() || 0}
                                    </button>
                                  </td>
                                  <td>{row.searchCount?.toLocaleString() || 0}</td>
                                  <td>{row.loggedInUsers || 0}</td>
                                  <td>{row.anonymousUsers || 0}</td>
                                  <td>{row.lastSearched ? formatDate(new Date(row.lastSearched)) : 'N/A'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* pagination bar */}
                    {imageSearchData.pagination && imageSearchData.pagination.totalCount > imageSearchRowsPerPage && (
                      <div className="search-log-pagination">
                        <span className="search-log-pagination-info">
                          Showing {(imageSearchCurrentPage - 1) * imageSearchRowsPerPage + 1}–{Math.min(imageSearchCurrentPage * imageSearchRowsPerPage, imageSearchData.pagination.totalCount)} of {imageSearchData.pagination.totalCount}
                        </span>
                        <div className="search-log-pagination-btns">
                          <button className="search-log-pg-btn" onClick={() => fetchImageSearch(imageSearchCurrentPage - 1, imageSearchRowsPerPage, imageSearchQuery)} disabled={imageSearchCurrentPage === 1 || imageSearchLoading}>Previous</button>
                          <span>Page {imageSearchCurrentPage} of {Math.ceil(imageSearchData.pagination.totalCount / imageSearchRowsPerPage)}</span>
                          <button className="search-log-pg-btn" onClick={() => fetchImageSearch(imageSearchCurrentPage + 1, imageSearchRowsPerPage, imageSearchQuery)} disabled={imageSearchCurrentPage >= Math.ceil(imageSearchData.pagination.totalCount / imageSearchRowsPerPage) || imageSearchLoading}>Next</button>
                        </div>
                      </div>
                    )}

                    {/* {(!imageSearchData.imageSearches || imageSearchData.imageSearches.length === 0) && imageSearchQuery && (
                      <div className="empty-state" style={{ marginTop: '12px' }}>
                        No image search data found for "{imageSearchQuery}"
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                          Try searching with a different username or email
                        </div>
                      </div>
                    )} */}
                  </>
              </>
            )}
          </div>
        )}
        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="security-content">
            <div className="content-grid">
              <TableCard
                title="Blocked Keywords"
                data={securityData?.blockedKeywords}
                loading={securityDataLoading}
                columns={['Keyword', 'Searches', 'Category', 'Action']}
                renderRow={(item, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{item.keyword}</strong>
                    </td>
                    <td>{item.searchCount}</td>
                    <td>
                      <span className="badge">{(item.category === 'alibaba' ? 'global' : item.category) || 'global'}</span>
                    </td>
                    <td>
                      <button className="search-log-btn-sm search-log-btn-blocked" onClick={() => handleBlockKeyword(item.keyword, true)} title="Click to unblock">
                        Unlocked
                      </button>
                    </td>
                  </tr>
                )}
                emptyMessage="No blocked keywords"
              />

              <TableCard
                title="Suspicious IPs"
                data={securityData?.suspiciousIPs}
                loading={securityDataLoading}
                columns={['IP Address', 'Total Searches', 'Unique Keywords', 'Risk Level']}
                renderRow={(item, idx) => {
                  let riskLevel = 'Low';
                  if (item.totalSearches > 1000 || item.uniqueKeywords > 200) {
                    riskLevel = 'High';
                  } else if (item.totalSearches > 500 || item.uniqueKeywords > 100) {
                    riskLevel = 'Medium';
                  }

                  return (
                    <tr key={idx}>
                      <td>
                        <code>{item.ip}</code>
                      </td>
                      <td>{item.totalSearches}</td>
                      <td>{item.uniqueKeywords}</td>
                      <td>
                        <span className={`badge ${riskLevel === 'High' ? 'badge-danger' : riskLevel === 'Medium' ? 'badge-warning' : 'badge-success'}`}>{riskLevel}</span>
                      </td>
                    </tr>
                  );
                }}
                emptyMessage="No suspicious IPs detected"
              />
            </div>
          </div>
        )}

        {/* Search Details Tab */}
        {activeTab === 'search' && (
          <div className="search-details-content">
            <div className="search-log-search-results">
              <div className="">
                <h3 className="table-title">
                  {searchResults && searchType === 'keyword' && (
                    <>
                      Users who searched: <strong>"{searchResults.keyword}"</strong>
                    </>
                  )}
                  {searchResults && searchType === 'email' && (
                    <>
                      Keywords searched by: <strong>"{searchResults.email}"</strong>
                    </>
                  )}
                  {searchResults && searchType === 'ip' && (
                    <>
                      Keywords searched from IP: <strong>"{searchResults.ip}"</strong>
                    </>
                  )}
                  {/* {!searchResults && <>Search Details</>} */}
                </h3>

                {searchResults && (
                  <div className="search-stats">
                    {searchType === 'keyword' ? (
                      <>
                        <span>
                          Total Searches: <strong>{searchResults.totalSearches}</strong>
                        </span>
                        <span>
                          Total Users: <strong>{searchResults.totalUsers}</strong>
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          Total Keywords: <strong>{searchResults.totalKeywords}</strong>
                        </span>
                        <span>
                          Total Searches: <strong>{searchResults.totalSearches}</strong>
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* Toolbar */}
                <div className="search-log-toolbar" style={{ marginBottom: '10px' }}>
                  <select
                    value={searchType}
                    onChange={(e) => { setSearchType(e.target.value); setSearchResults(null); setSearchQuery(''); setSearchDetailsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="keyword">Search by Keyword</option>
                    <option value="email">Search by Email</option>
                    <option value="ip">Search by IP</option>
                  </select>
                  <div className="search-log-search-wrap" style={{ minWidth: '220px', maxWidth: '300px' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                      placeholder={searchType === 'keyword' ? 'Enter keyword...' : searchType === 'email' ? 'Enter email...' : 'Enter IP address...'}
                      className="search-log-search-input"
                      style={{ paddingLeft: '8px' }}
                    />
                    {searchQuery && (
                      <FiX onClick={() => { setSearchQuery(''); setSearchResults(null); setSearchDetailsCurrentPage(1); }} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }} />
                    )}
                  </div>
                  <button
                    className="search-log-action-btn search-log-action-btn--outline"
                    onClick={handleSearch}
                    disabled={searchLoading || !searchQuery.trim()}
                  >
                    <FiSearch /> {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                  <label style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Rows:</label>
                  <select
                    value={searchDetailsRowsPerPage}
                    onChange={(e) => { setSearchDetailsRowsPerPage(Number(e.target.value)); setSearchDetailsCurrentPage(1); }}
                    className="search-log-action-btn search-log-action-btn--outline"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                {/* Table */}
                <div className="search-log-tbl-wrap">
                  <table className="search-log-tbl">
                    <thead>
                      <tr>
                        {searchType === 'keyword' ? (
                          <>
                            <th>Email</th>
                            <th>IP Address</th>
                            <th>Search Count</th>
                            <th>Last Searched</th>
                            <th>Status</th>
                          </>
                        ) : searchType === 'email' ? (
                          <>
                            <th>Keyword</th>
                            <th>Search Count</th>
                            <th>IP Address</th>
                            <th>Last Searched</th>
                          </>
                        ) : (
                          <>
                            <th>Keyword</th>
                            <th>Search Count</th>
                            <th>Last Searched</th>
                            <th>Users</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {searchLoading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                          <tr key={i} className="search-log-tbl-loading">
                            {Array.from({ length: searchType === 'keyword' ? 5 : 4 }).map((__, j) => (
                              <td key={j}><span className="search-log-skeleton" /></td>
                            ))}
                          </tr>
                        ))
                      ) : !searchResults || paginatedSearchDetailsData.length === 0 ? (
                        <tr className="search-log-tbl-empty">
                          <td colSpan={searchType === 'keyword' ? 5 : 4}>
                            {searchResults ? 'No results found' : 'Enter a search term above to see results'}
                          </td>
                        </tr>
                      ) : searchType === 'keyword' ? (
                        paginatedSearchDetailsData.map((row, idx) => (
                          <tr key={idx}>
                            <td><strong>{row.email || 'N/A'}</strong></td>
                            <td><code style={{ fontSize: '11px' }}>{row.ip || 'N/A'}</code></td>
                            <td>
                              <span onClick={() => handleSearchCountTimestampsClick(row.searchTimestamps, 'keyword', row.searchCount)} style={{ cursor: 'pointer', color: '#3b82f6' }}>
                                {row.searchCount || 0}
                              </span>
                            </td>
                            <td>{row.lastSearched ? formatDate(new Date(row.lastSearched)) : 'N/A'}</td>
                            <td><span className={`badge ${row.isLoggedIn ? 'badge-success' : 'badge-secondary'}`}>{row.isLoggedIn ? 'Logged In' : 'Anonymous'}</span></td>
                          </tr>
                        ))
                      ) : searchType === 'email' ? (
                        paginatedSearchDetailsData.map((row, idx) => (
                          <tr key={idx}>
                            <td><strong>{row.keyword}</strong></td>
                            <td>
                              <span onClick={() => handleSearchCountTimestampsClick(row.searchTimestamps, 'email', row.searchCount)} style={{ cursor: 'pointer', color: '#3b82f6' }}>
                                {row.searchCount || 0}
                              </span>
                            </td>
                            <td><code style={{ fontSize: '11px' }}>{row.ip || 'N/A'}</code></td>
                            <td>{row.lastSearched ? formatDate(new Date(row.lastSearched)) : 'N/A'}</td>
                          </tr>
                        ))
                      ) : (
                        paginatedSearchDetailsData.map((row, idx) => (
                          <tr key={idx}>
                            <td><strong>{row.keyword}</strong></td>
                            <td>
                              <span onClick={() => handleSearchCountTimestampsClick(row.searchTimestamps, 'ip', row.searchCount)} style={{ cursor: 'pointer', color: '#3b82f6' }}>
                                {row.searchCount || 0}
                              </span>
                            </td>
                            <td>{row.lastSearched ? formatDate(new Date(row.lastSearched)) : 'N/A'}</td>
                            <td>
                              {Array.isArray(row.users) && row.users.length > 0 ? (
                                <div>
                                  {row.users.map((user, ui) => (
                                    <div key={ui} style={{ marginBottom: '3px' }}>
                                      <span className={`badge ${user.isLoggedIn ? 'badge-success' : 'badge-secondary'}`}>{user.email || 'Anonymous'}</span>
                                      <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '4px' }}>({user.searchCount || 0} searches)</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="badge badge-secondary">Anonymous</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {searchResults && searchDetailsData.length > searchDetailsRowsPerPage && (
                  <div className="search-log-pagination">
                    <span className="search-log-pagination-info">
                      Showing {(searchDetailsCurrentPage - 1) * searchDetailsRowsPerPage + 1}–{Math.min(searchDetailsCurrentPage * searchDetailsRowsPerPage, searchDetailsData.length)} of {searchDetailsData.length}
                    </span>
                    <div className="search-log-pagination-btns">
                      <button className="search-log-pg-btn" onClick={() => setSearchDetailsCurrentPage(searchDetailsCurrentPage - 1)} disabled={searchDetailsCurrentPage === 1 || searchLoading}>Previous</button>
                      <span>Page {searchDetailsCurrentPage} of {searchDetailsTotalPages}</span>
                      <button className="search-log-pg-btn" onClick={() => setSearchDetailsCurrentPage(searchDetailsCurrentPage + 1)} disabled={searchDetailsCurrentPage >= searchDetailsTotalPages || searchLoading}>Next</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* {!searchResults && !searchLoading && (
              <div className="search-placeholder">
                <FiSearch size={48} />
                <p>Enter a {searchType === 'keyword' ? 'keyword' : searchType === 'email' ? 'email' : 'IP address'} to search</p>
              </div>
            )} */}
          </div>
        )}

        {/* Product Intent Tab */}
        {activeTab === 'product-intent' && (
          <div className="product-intent-content">

            {/* Toolbar — always visible */}
            <div className="search-log-toolbar" style={{ marginBottom: '10px' }}>
              {/* Search */}
              <div className="search-log-search-wrap" style={{ minWidth: '200px', maxWidth: '280px' }}>
                <FiSearch style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontSize: '11px', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Search by username or email..."
                  value={productIntentSearchQuery}
                  onChange={(e) => setProductIntentSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setProductIntentCurrentPage(1); fetchProductIntentData(1, productIntentSearchQuery.trim()); } }}
                  className="search-log-search-input"
                />
                {productIntentSearchQuery && (
                  <FiX onClick={() => { setProductIntentSearchQuery(''); fetchProductIntentData(1, ''); }} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }} />
                )}
              </div>
              <button
                className="search-log-action-btn search-log-action-btn--outline"
                onClick={() => { setProductIntentCurrentPage(1); fetchProductIntentData(1, productIntentSearchQuery.trim()); }}
                disabled={productIntentLoading}
              >
                <FiSearch /> Search
              </button>
              {/* Sort by */}
              <select
                value={productIntentSortBy}
                onChange={(e) => { setProductIntentSortBy(e.target.value); setProductIntentCurrentPage(1); }}
                className="search-log-action-btn search-log-action-btn--outline"
                style={{ cursor: 'pointer' }}
              >
                <option value="mostPdpOpens">Most PDP Opens</option>
                <option value="mostAddToCart">Most Add to Cart</option>
                <option value="mostCurrentAddToCart">Most Current Cart</option>
                <option value="mostSearchKeywords">Most Search Keywords</option>
                <option value="recentFirst">Recent First</option>
              </select>
              {/* User type */}
              <select
                value={userTypeFilter}
                onChange={(e) => { setUserTypeFilter(e.target.value); setProductIntentCurrentPage(1); }}
                className="search-log-action-btn search-log-action-btn--outline"
                style={{ cursor: 'pointer' }}
              >
                <option value="all">All Users</option>
                <option value="logged-in">Login Users</option>
                <option value="unknown">Unknown Users</option>
              </select>
              <label style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Rows:</label>
              <select
                value={productIntentRowsPerPage}
                onChange={(e) => { setProductIntentRowsPerPage(Number(e.target.value)); setProductIntentCurrentPage(1); }}
                className="search-log-action-btn search-log-action-btn--outline"
                style={{ cursor: 'pointer' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                className="search-log-action-btn search-log-action-btn--outline"
                onClick={() => fetchProductIntentData(1)}
                disabled={productIntentLoading}
                title="Refresh"
              >
                <FiRefreshCcw className={productIntentLoading ? 'spinning' : ''} />
              </button>
            </div>

            {productIntentLoading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading analytics...</p>
              </div>
            ) : (
              <>
                {productIntentUsers && productIntentUsers.users ? (() => {
                  const users = productIntentUsers.users || [];
                  return users.length > 0 ? (
                    <>

                  {/* Table */}
                  <div className="search-log-tbl-wrap">
                    <table className="search-log-tbl">
                      <thead>
                        <tr>
                          <th>User Email</th>
                          <th>Phone</th>
                          <th>PDP Opens</th>
                          <th>Add to Cart</th>
                          <th>Current Add to Cart</th>
                          <th>Search Keyword</th>
                          <th>Last Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productIntentLoading ? (
                          Array.from({ length: 6 }).map((_, i) => (
                            <tr key={i} className="search-log-tbl-loading">
                              {Array.from({ length: 7 }).map((__, j) => <td key={j}><span className="search-log-skeleton" /></td>)}
                            </tr>
                          ))
                        ) : users.length === 0 ? (
                          <tr className="search-log-tbl-empty"><td colSpan={7}>No product intent data found</td></tr>
                        ) : (
                          users.map((item, idx) => {
                            const lastActivity = (() => {
                              const candidates = [item.lastPdpOpenAt, item.lastAddToCartAt].filter(x => x != null && x !== '');
                              let best = null, bestT = -Infinity;
                              for (const c of candidates) {
                                const parts = Array.isArray(c) ? c : [c];
                                for (const val of parts) {
                                  const t = new Date(val).getTime();
                                  if (!Number.isNaN(t) && t > bestT) { bestT = t; best = val; }
                                }
                              }
                              return best;
                            })();
                            const firstName = item.firstName || '';
                            const lastName = item.lastName || '';
                            const userName = item.userName || '';
                            const displayName = firstName && lastName ? `${firstName} ${lastName}` : userName || '';
                            const email = item.email || item.ip || 'Anonymous';
                            const phone = item.phone || '-';
                            const uniqueId = item.uniqueId || '';
                            const userId = item.userId || item.ip;
                            const cartCount = item.currentCartCount || 0;
                            const hasUserId = item.userId && item.userId !== item.ip && !item.userId.match(/^\d+\.\d+\.\d+\.\d+$/);
                            const isCartClickable = hasUserId && cartCount > 0;
                            const keywordsCount = item.searchKeywordsCount || 0;
                            const canFetch = (item.email && !item.email.match(/^\d+\.\d+\.\d+\.\d+$/)) || item.userId || item.ip;
                            const isKwClickable = canFetch && keywordsCount > 0;
                            const identifier = item.email && !item.email.match(/^\d+\.\d+\.\d+\.\d+$/) ? item.email : item.ip;
                            return (
                              <tr key={idx}>
                                <td>
                                  {displayName && <div style={{ fontWeight: '600', marginBottom: '2px' }}>{displayName}</div>}
                                  <div style={{ fontSize: '11px', color: '#64748b' }}>{email}</div>
                                </td>
                                <td>
                                  <div>{phone}</div>
                                  {uniqueId && <div style={{ fontSize: '10px', color: '#94a3b8' }}>({uniqueId})</div>}
                                </td>
                                <td>
                                  <span
                                    style={{ cursor: item.pdpOpenCount > 0 ? 'pointer' : 'default', color: item.pdpOpenCount > 0 ? '#3b82f6' : 'inherit', textDecoration: item.pdpOpenCount > 0 ? 'underline' : 'none' }}
                                    onClick={(e) => { e.stopPropagation(); if (item.pdpOpenCount > 0 && userId) openUserProductDetailsInNewTab(userId, 'pdp_open', item.email || item.ip); }}
                                  >
                                    {productIntentDetailLoading[`${userId}_pdp_open`] ? <div className="small-spinner"></div> : (item.pdpOpenCount || 0)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{ cursor: item.addToCartCount > 0 ? 'pointer' : 'default', color: item.addToCartCount > 0 ? '#3b82f6' : 'inherit', textDecoration: item.addToCartCount > 0 ? 'underline' : 'none' }}
                                    onClick={(e) => { e.stopPropagation(); if (item.addToCartCount > 0 && userId) openUserProductDetailsInNewTab(userId, 'add_to_cart', item.email || item.ip); }}
                                  >
                                    {productIntentDetailLoading[`${userId}_add_to_cart`] ? <div className="small-spinner"></div> : (item.addToCartCount || 0)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{ cursor: isCartClickable ? 'pointer' : 'default', color: isCartClickable ? '#3b82f6' : 'inherit', textDecoration: isCartClickable ? 'underline' : 'none' }}
                                    onClick={(e) => { e.stopPropagation(); if (hasUserId) { cartCount > 0 ? openUserCartInNewTab(item.userId, item.email || item.ip) : message.showToast('No items in cart', ToastTypes.INFO); } else { message.showToast('Cart data not available for anonymous users', ToastTypes.WARNING); } }}
                                  >
                                    {productIntentDetailLoading[`${item.userId}_cart`] ? <div className="small-spinner"></div> : (cartCount || 0)}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{ cursor: isKwClickable ? 'pointer' : 'default', color: isKwClickable ? '#3b82f6' : 'inherit', textDecoration: isKwClickable ? 'underline' : 'none' }}
                                    onClick={(e) => { e.stopPropagation(); if (canFetch) { keywordsCount > 0 ? openUserSearchKeywordsInNewTab(identifier, item.userId) : message.showToast('No search keywords found', ToastTypes.INFO); } else { message.showToast('Search keywords not available', ToastTypes.WARNING); } }}
                                  >
                                    {productIntentDetailLoading[`${item.userId || identifier}_searchKeywords`] ? <div className="small-spinner"></div> : keywordsCount}
                                  </span>
                                </td>
                                <td>{lastActivity ? formatDate(new Date(lastActivity)) : 'N/A'}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  {productIntentUsers && productIntentUsers.totalCount > productIntentRowsPerPage && (
                    <div className="search-log-pagination">
                      <span className="search-log-pagination-info">
                        Showing {(productIntentCurrentPage - 1) * productIntentRowsPerPage + 1}–{Math.min(productIntentCurrentPage * productIntentRowsPerPage, productIntentUsers.totalCount)} of {productIntentUsers.totalCount}
                      </span>
                      <div className="search-log-pagination-btns">
                        <button className="search-log-pg-btn" onClick={() => fetchProductIntentData(productIntentCurrentPage - 1)} disabled={productIntentCurrentPage === 1 || productIntentLoading}>Previous</button>
                        <span>Page {productIntentCurrentPage} of {Math.ceil(productIntentUsers.totalCount / productIntentRowsPerPage)}</span>
                        <button className="search-log-pg-btn" onClick={() => fetchProductIntentData(productIntentCurrentPage + 1)} disabled={productIntentCurrentPage >= Math.ceil(productIntentUsers.totalCount / productIntentRowsPerPage) || productIntentLoading}>Next</button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                  <div className="empty-state">
                    <p>No product intent data available for the selected filter.</p>
                  </div>
              );
                })() : (
                  <div className="empty-state">
                    <p>No product intent data available for the selected filter.</p>
                  </div>
                )}
              </>
            )}

            {/* User Product Details Modal */}
            {userProductModal.open && (
              <div className="user-product-modal-backdrop" onClick={() => setUserProductModal({ open: false, userId: null, eventType: null, userEmail: null, products: [], loading: false })}>
                <div className="user-product-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="user-product-modal-header">
                    <h3>
                      {userProductModal.eventType === 'pdp_open' ? 'PDP Opens' : 'Add to Cart'} - {userProductModal.userEmail || 'User'}
                    </h3>
                    <button
                      className="modal-close-btn"
                      onClick={() => setUserProductModal({ open: false, userId: null, eventType: null, userEmail: null, products: [], loading: false })}
                    >
                      <FiX />
                    </button>
                  </div>
                  <div className="user-product-modal-content">
                    {userProductModal.loading ? (
                      <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading products...</p>
                      </div>
                    ) : userProductModal.products && userProductModal.products.length > 0 ? (
                      <div className="search-log-tbl-wrap">
                        <table className="search-log-tbl">
                          <thead>
                            <tr>
                              <th>Image</th>
                              <th>SKU</th>
                              <th>Product Title</th>
                              <th>SP</th>
                              {userProductModal.eventType === 'add_to_cart' && <><th>Quantity</th><th>Type</th></>}
                              <th>{userProductModal.eventType === 'pdp_open' ? 'Opens' : 'Adds'}</th>
                              <th>Last Activity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userProductModal.products.map((p, idx) => (
                              <tr key={idx}>
                                <td>
                                  {p.imgUrl ? (
                                    <img src={p.imgUrl} alt="Product" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
                                  ) : (
                                    <div style={{ width: '48px', height: '48px', background: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af' }}>No Image</div>
                                  )}
                                </td>
                                <td>{p.sku}</td>
                                <td>{p.title}</td>
                                <td>{p.sp ? `₹${parseFloat(p.sp).toFixed(2)}` : 'N/A'}</td>
                                {userProductModal.eventType === 'add_to_cart' && (
                                  <>
                                    <td>{p.totalQuantity || 0}</td>
                                    <td>
                                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: p.productType === 'DS' ? '#dbeafe' : '#fef3c7', color: p.productType === 'DS' ? '#1e40af' : '#92400e' }}>
                                        {p.productType || 'N/A'}
                                      </span>
                                    </td>
                                  </>
                                )}
                                <td>{p.count}</td>
                                <td>{p.lastTimestamp ? formatDate(new Date(p.lastTimestamp)) : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No products found for this user.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* User Cart Modal */}
            {userCartModal.open && (
              <div className="user-product-modal-backdrop" onClick={() => setUserCartModal({ open: false, userId: null, userEmail: null, cartItems: [], loading: false })}>
                <div className="user-product-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="user-product-modal-header">
                    <h3>
                      Current Add to Cart - {userCartModal.userEmail || 'User'}
                    </h3>
                    <button
                      className="modal-close-btn"
                      onClick={() => setUserCartModal({ open: false, userId: null, userEmail: null, cartItems: [], loading: false })}
                    >
                      <FiX />
                    </button>
                  </div>
                  <div className="user-product-modal-content">
                    {userCartModal.loading ? (
                      <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading cart...</p>
                      </div>
                    ) : userCartModal.cartItems && userCartModal.cartItems.length > 0 ? (
                      <div className="search-log-tbl-wrap">
                        <table className="search-log-tbl">
                          <thead>
                            <tr>
                              <th>Image</th>
                              <th>Product SKU</th>
                              <th>Product Title</th>
                              <th>Product SP</th>
                              <th>Variant SKU ID</th>
                              <th>Variant Spec ID</th>
                              <th>Variant Price</th>
                              <th>Quantity</th>
                              <th>Type</th>
                              <th>Weight (kg)</th>
                              <th>Dimensions</th>
                              <th>Variant Attributes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userCartModal.cartItems.map((p, idx) => (
                              <tr key={idx}>
                                <td>
                                  {p.imgUrl ? (
                                    <img src={p.imgUrl} alt="Product" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
                                  ) : (
                                    <div style={{ width: '48px', height: '48px', background: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#9ca3af' }}>No Image</div>
                                  )}
                                </td>
                                <td>{p.sku || 'N/A'}</td>
                                <td>{p.title || 'N/A'}</td>
                                <td>{p.sp ? `₹${parseFloat(p.sp).toFixed(2)}` : 'N/A'}</td>
                                <td>{p.variantSkuId !== 'N/A' ? p.variantSkuId : 'N/A'}</td>
                                <td>{p.variantSpecId !== 'N/A' ? <code style={{ fontSize: '10px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{p.variantSpecId}</code> : 'N/A'}</td>
                                <td>{p.variantFinalPrice > 0 ? `₹${parseFloat(p.variantFinalPrice).toFixed(2)}` : 'N/A'}</td>
                                <td>{p.quantity || 0}</td>
                                <td>
                                  <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '600', backgroundColor: p.pricingMode === 'dropshipping' ? '#dbeafe' : '#fef3c7', color: p.pricingMode === 'dropshipping' ? '#1e40af' : '#92400e' }}>
                                    {p.pricingMode === 'dropshipping' ? 'DS' : p.pricingMode === 'wholesale' ? 'WS' : p.pricingMode || 'N/A'}
                                  </span>
                                </td>
                                <td>{p.variantWeight > 0 ? parseFloat(p.variantWeight).toFixed(2) : 'N/A'}</td>
                                <td>{p.variantDimensions !== 'N/A' ? p.variantDimensions : 'N/A'}</td>
                                <td>
                                  {p.variantAttributes && Array.isArray(p.variantAttributes) && p.variantAttributes.length > 0 ? (
                                    <div style={{ fontSize: '10px' }}>
                                      {p.variantAttributes.map((attr, ai) => (
                                        <div key={ai} style={{ marginBottom: '2px' }}>
                                          <strong>{attr.name || attr.key}:</strong> {attr.value || attr.val || 'N/A'}
                                        </div>
                                      ))}
                                    </div>
                                  ) : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No products in cart for this user.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* User Search Keywords Modal */}
            {userSearchKeywordsModal.open && (
              <div className="user-product-modal-backdrop" onClick={() => setUserSearchKeywordsModal({ open: false, userId: null, userEmail: null, keywords: [], loading: false })}>
                <div className="user-product-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="user-product-modal-header">
                    <h3>
                      Search Keywords - {userSearchKeywordsModal.userEmail || 'User'}
                    </h3>
                    <button
                      className="modal-close-btn"
                      onClick={() => setUserSearchKeywordsModal({ open: false, userId: null, userEmail: null, keywords: [], loading: false })}
                    >
                      <FiX />
                    </button>
                  </div>
                  <div className="user-product-modal-content">
                    {userSearchKeywordsModal.loading ? (
                      <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading search keywords...</p>
                      </div>
                    ) : userSearchKeywordsModal.keywords && userSearchKeywordsModal.keywords.length > 0 ? (
                      <div className="search-log-tbl-wrap">
                        <table className="search-log-tbl">
                          <thead>
                            <tr>
                              <th>Keyword</th>
                              <th>Search Count</th>
                              <th>IP Address</th>
                              <th>Last Searched</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userSearchKeywordsModal.keywords.map((row, idx) => {
                              const keywordId = `${row.keyword}-${row.ip || 'unknown'}`;
                              const timestamps = row.searchTimestamps || [];
                              return (
                                <tr key={idx}>
                                  <td><strong>{row.keyword}</strong></td>
                                  <td>
                                    <div
                                      onMouseEnter={() => setHoveredKeywordId(keywordId)}
                                      onMouseLeave={() => setHoveredKeywordId(null)}
                                      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                    >
                                      <span style={{ color: '#3b82f6', textDecoration: 'underline' }}>{row.searchCount || 0}</span>
                                      {hoveredKeywordId === keywordId && timestamps.length > 0 && (
                                        <div className="keyword-hover-card">
                                          <div className="keyword-card-header">SEARCH TIMESTAMPS ({timestamps.length})</div>
                                          <div className="keyword-card-content">
                                            {timestamps.map((ts, tsIdx) => (
                                              <div key={tsIdx} style={{ padding: '4px 0', borderBottom: tsIdx < timestamps.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                                {formatDate(new Date(ts))}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td><code style={{ fontSize: '11px' }}>{row.ip}</code></td>
                                  <td>{row.lastSearched ? formatDate(new Date(row.lastSearched)) : 'N/A'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No search keywords found for this user.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Search Count Timestamps Modal */}
        {searchCountTimestampsModal.open && (
          <div className="user-product-modal-backdrop" onClick={() => setSearchCountTimestampsModal({ open: false, timestamps: [], searchType: '', value: 0 })}>
            <div className="search-timestamps-modal" onClick={(e) => e.stopPropagation()}>
              <div className="search-timestamps-modal-header">
                <h3 className="search-timestamps-modal-title">
                  Search Timestamps ({searchCountTimestampsModal.timestamps?.length || 0})
                </h3>
                <button
                  className="search-timestamps-modal-close"
                  onClick={() => setSearchCountTimestampsModal({ open: false, timestamps: [], searchType: '', value: 0 })}
                >
                  <FiX />
                </button>
              </div>
              <div className="search-timestamps-modal-content">
                {searchCountTimestampsModal.timestamps && searchCountTimestampsModal.timestamps.length > 0 ? (
                  <div className="search-timestamps-list">
                    {searchCountTimestampsModal.timestamps.map((timestamp, idx) => (
                      <div key={idx} className="search-timestamps-item">
                        {formatDate(new Date(timestamp))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No timestamps found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchLog;
